/*
 * EverQuilts cart drawer extras (08.09.2026)
 *
 * Dawn's cart drawer supplies the panel, the line items, the quantity steppers
 * and the checkout button. This file layers on top of it:
 *   - a Bundle & Save headline band
 *   - an "add N more to get $X off" progress bar driven by the REAL bundle
 *     tiers in snippets/eq-bundle-save.liquid (3 -> $7, 4 -> $10, 5+ -> $15)
 *   - a "Customer's Also Loved" row from Shopify's recommendations API
 *   - the order total on the checkout button + the real 30-day guarantee line
 *
 * Dawn rebuilds the drawer contents on every cart change, so everything here
 * is re-applied by a MutationObserver. Re-entry is controlled by a signature
 * of the cart state rather than a one-shot flag, which keeps the pass
 * idempotent: our own DOM writes do not change the signature, so they cannot
 * trigger another pass, but a real cart change always does.
 *
 * The re-run is queued with setTimeout, not requestAnimationFrame: rAF is
 * paused while the tab is hidden, which would leave the drawer undecorated
 * for anyone who adds to cart in a background tab.
 */
(function () {
  'use strict';

  var TIERS = [
    { qty: 3, off: 7 },
    { qty: 4, off: 10 },
    { qty: 5, off: 15 }
  ];
  var TOP = TIERS[TIERS.length - 1];
  var OURS = '.eqcd-banner, .eqcd-progress, .eqcd-recs, .eqcd-guarantee';

  /* Promises, not results: a failed lookup must not be cached as "no
     recommendations" for the rest of the page session. */
  var recsPromises = {};

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function priceLabel(p) {
    var v = p.price;
    if (typeof v === 'string') {
      if (v.indexOf('.') > -1) return '$' + parseFloat(v).toFixed(2);
      v = parseInt(v, 10);
    }
    if (typeof v !== 'number' || isNaN(v)) return '';
    return '$' + (v / 100).toFixed(2);
  }

  function imgUrl(p) {
    var i = p.featured_image || (p.images && p.images[0]);
    if (!i) return '';
    var u = typeof i === 'string' ? i : i.src || '';
    if (!u) return '';
    if (u.indexOf('//') === 0) u = 'https:' + u;
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=400';
  }

  function readCart(inner) {
    var count = 0;
    var handles = [];
    inner.querySelectorAll('.quantity__input').forEach(function (input) {
      var n = parseInt(input.value, 10);
      if (!isNaN(n)) count += n;
    });
    inner.querySelectorAll('.cart-item__name').forEach(function (a) {
      var m = (a.getAttribute('href') || '').match(/\/products\/([^?#\/]+)/);
      if (m) handles.push(m[1]);
    });
    var total = inner.querySelector('.totals__total-value');
    return {
      count: count,
      handles: handles,
      total: total ? total.textContent.trim() : '',
      empty: !!inner.querySelector('.drawer__inner-empty')
    };
  }

  function signature(state) {
    return state.empty + '|' + state.count + '|' + state.total + '|' + state.handles.join(',');
  }

  /* ---------------------------------------------------------------- band */

  function banner() {
    return el(
      'div',
      'eqcd-banner',
      '<span class="eqcd-banner__title">Bundle &amp; Save</span>' +
        '<span class="eqcd-banner__tiers">3 tees $7 off &middot; 4 tees $10 off &middot; 5+ tees $15 off</span>'
    );
  }

  /* ------------------------------------------------------------ progress */

  function progress(count) {
    var next = null;
    for (var i = 0; i < TIERS.length; i++) {
      if (count < TIERS[i].qty) {
        next = TIERS[i];
        break;
      }
    }

    var wrap = el('div', 'eqcd-progress');
    var msg;

    if (!next) {
      wrap.classList.add('is-complete');
      msg = 'Nice haul — $' + TOP.off + ' off is unlocked on this order';
    } else {
      var need = next.qty - count;
      msg =
        'Add ' + need + ' more item' + (need === 1 ? '' : 's') +
        ' to get $' + next.off + ' off your order';
    }

    var pct = Math.min(count / TOP.qty, 1) * 100;
    wrap.innerHTML =
      '<p class="eqcd-progress__msg">' + msg + '</p>' +
      '<div class="eqcd-progress__track">' +
      '<div class="eqcd-progress__fill" style="width:' + pct.toFixed(1) + '%"></div>' +
      '</div>';
    return wrap;
  }

  /* ----------------------------------------------------- recommendations */

  function fetchRecs(handle) {
    if (recsPromises[handle]) return recsPromises[handle];

    var p = fetch('/products/' + handle + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('product lookup ' + r.status);
        return r.json();
      })
      .then(function (product) {
        return fetch(
          '/recommendations/products.json?product_id=' + product.id + '&limit=8&intent=related'
        );
      })
      .then(function (r) {
        if (!r.ok) throw new Error('recommendations ' + r.status);
        return r.json();
      })
      .then(function (data) {
        return (data && data.products) || [];
      })
      .catch(function (err) {
        /* Drop the failed promise so the next pass can retry. */
        delete recsPromises[handle];
        if (window.console) console.warn('[eq-cart-drawer] recommendations failed:', err);
        return [];
      });

    recsPromises[handle] = p;
    return p;
  }

  function renderRecs(container, products, inCart) {
    var picks = products
      .filter(function (p) {
        return inCart.indexOf(p.handle) === -1;
      })
      .slice(0, 3);

    if (!picks.length) {
      container.hidden = true;
      return;
    }

    var cards = picks
      .map(function (p) {
        var src = imgUrl(p);
        return (
          '<a class="eqcd-rec" href="' + (p.url || '/products/' + p.handle) + '">' +
          (src
            ? '<img class="eqcd-rec__img" src="' + src + '" alt="" width="200" height="200">'
            : '<span class="eqcd-rec__img"></span>') +
          '<span class="eqcd-rec__title">' + (p.title || '') + '</span>' +
          '<span class="eqcd-rec__price">' + priceLabel(p) + '</span>' +
          '</a>'
        );
      })
      .join('');

    container.innerHTML =
      '<h3 class="eqcd-recs__heading">Customer’s Also Loved</h3>' +
      '<div class="eqcd-recs__row">' + cards + '</div>';
    container.hidden = false;
  }

  /* ------------------------------------------------------------ decorate */

  function decorate() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    var inner = drawer.querySelector('.drawer__inner');
    if (!inner) return;

    var state = readCart(inner);
    var sig = signature(state);

    /* Nothing changed and our markup is still in place: leave it alone. */
    if (inner.dataset.eqSig === sig && inner.querySelector('.eqcd-banner')) return;
    inner.dataset.eqSig = sig;

    inner.querySelectorAll(OURS).forEach(function (n) {
      n.remove();
    });

    inner.insertBefore(banner(), inner.firstChild);
    if (state.empty) return;

    var header = inner.querySelector('.drawer__header');
    if (header && header.parentNode) {
      header.parentNode.insertBefore(progress(state.count), header.nextSibling);
    }

    var items = inner.querySelector('#CartDrawer-CartItems');
    if (items && state.handles.length) {
      var recs = el('div', 'eqcd-recs');
      recs.hidden = true;
      items.appendChild(recs);
      fetchRecs(state.handles[0]).then(function (products) {
        if (recs.isConnected) renderRecs(recs, products, state.handles);
      });
    }

    var checkout = inner.querySelector('#CartDrawer-Checkout');
    if (checkout && state.total) {
      checkout.textContent = 'Checkout • ' + state.total;
    }

    var ctas = inner.querySelector('.cart__ctas');
    if (ctas && ctas.parentNode) {
      ctas.parentNode.insertBefore(
        el('p', 'eqcd-guarantee', '30-Day Guarantee — damaged or misprinted, we replace it free'),
        ctas.nextSibling
      );
    }
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    setTimeout(function () {
      queued = false;
      decorate();
    }, 0);
  }

  function start() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    decorate();
    new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
